/**
 * Cupones: los códigos de descuento del proyecto, en Ventas.
 *
 * La lista y sus acciones viven aquí; crear y editar, en `CouponDialog`. El
 * descuento lo aplica Stripe en el checkout —ver `services/coupons.ts` en el
 * worker—, así que sin cuenta conectada la sección explica qué falta en vez de
 * dejar crear algo que ningún checkout podría usar.
 *
 * Funciona en las páginas reales sin tocar nada del sitio: el checkout enseña
 * el campo «código promocional» cuando hay cupones que valen para esa oferta, y
 * un enlace con `?cupon=CODIGO` lo aplica solo.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  IconCalendarEvent,
  IconCopy,
  IconDots,
  IconLink,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconSearch,
  IconTicket,
  IconTrash,
} from '@tabler/icons-react'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { SectionRefresh } from '../../SectionRefresh'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useI18n } from '../../../lib/i18n'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import {
  deleteCoupon,
  getStripeConnection,
  listCoupons,
  listOffers,
  updateCoupon,
  type ConnectStatus,
  type Coupon,
} from '../../../lib/api'
import { HeaderRefreshButton } from '../ListSelection'
import { ConfirmDialog } from '../ConfirmDialog'
import { StripeConnectBanner } from './StripeConnectBanner'
import { CouponDialog, couponInputOf, couponShareLink } from './CouponDialog'
import { errorMessage, failureDetail, hasPaidOffer } from './offerShared'
import { useToast } from '../../Toast'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

const STATUS_TONE: Record<Coupon['status'], string> = {
  active: 'is-active',
  inactive: 'is-draft',
  expired: 'is-review',
  exhausted: 'is-partial',
}

export function CouponsSection({ projectId, token, siteOrigin, canConnectStripe = true, onOpenStripeSettings, onOpenPlans }: {
  projectId: string
  token: string
  /** El origen publicado del sitio, para armar enlaces con el cupón puesto. */
  siteOrigin: string | null
  canConnectStripe?: boolean
  onOpenStripeSettings: () => void
  onOpenPlans?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('coupons.search')
  const { showToast } = useToast()
  const coupons = useSectionResource(token, [projectId, 'coupons'], () => listCoupons(projectId, token))
  const offers = useSectionResource(token, [projectId, 'offers'], () => listOffers(projectId, token))
  const loader = useRecordLoader(coupons.loading)
  const [status, setStatus] = useState<ConnectStatus | null>(null)
  const [statusFailed, setStatusFailed] = useState('')
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Coupon | 'new' | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Coupon | null>(null)
  const [menu, setMenu] = useState<{ id: string; top: number; left: number; up: boolean } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const loadStatus = () => {
    getStripeConnection(projectId, token)
      .then((result) => { setStatus(result.status); setStatusFailed('') })
      .catch((error: unknown) => setStatusFailed(failureDetail(error)))
  }
  useEffect(loadStatus, [projectId, token])

  useEffect(() => {
    if (!menu) return
    const close = (event: MouseEvent) => {
      /* El propio «⋯» no cuenta como fuera: su clic ya abre y cierra, y si
         esto lo cerrara antes, el clic lo volvería a abrir. */
      const target = event.target as Element
      if (target.closest?.('[data-coupon-menu-trigger]')) return
      if (menuRef.current && !menuRef.current.contains(target)) setMenu(null)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenu(null) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const connected = status === 'ready' || status === 'restricted'
  const offerList = useMemo(() => offers.data ?? [], [offers.data])
  const offerById = useMemo(() => new Map(offerList.map((offer) => [offer.id, offer])), [offerList])
  const rows = coupons.data ?? []
  const visible = rows.filter((coupon) => {
    const needle = query.trim().toLowerCase()
    return !needle || matchesSearch(coupon.code, needle) || matchesSearch(coupon.description, needle)
  })

  if (loader.visible) return <SectionLoader loader={loader} label={t('coupons.loading')} />

  const appliesLabel = (coupon: Coupon) => {
    if (coupon.appliesToAll) return t('coupons.appliesAll')
    if (coupon.offerIds.length === 1) return offerById.get(coupon.offerIds[0] ?? '')?.title ?? t('coupons.appliesOne')
    return t('coupons.appliesMany', { count: coupon.offerIds.length })
  }

  /**
   * Cuántas veces se ha usado, en dos versiones.
   *
   * En la tabla ancha el número solo basta —lo dice la cabecera de la
   * columna, «Uses», justo encima—; con la palabra puesta ahí no cabría en
   * los 76px de la columna. En la ficha del teléfono esa cabecera desaparece,
   * y un «0» suelto no dice de qué es, así que ahí hace falta la frase
   * entera. Las dos van en el marcado —como el día y la hora de Reservas— y
   * el CSS del ancho decide cuál se ve.
   */
  const usesCompact = (coupon: Coupon) => coupon.maxRedemptions ? `${coupon.uses} / ${coupon.maxRedemptions}` : coupon.uses
  const usesFull = (coupon: Coupon) => coupon.maxRedemptions
    ? t(coupon.maxRedemptions === 1 ? 'coupons.usesLimitOne' : 'coupons.usesLimitMany', { uses: coupon.uses, max: coupon.maxRedemptions })
    : t(coupon.uses === 1 ? 'coupons.usesOne' : 'coupons.usesMany', { count: coupon.uses })

  const detailLabel = (coupon: Coupon) => [
    coupon.duration === 'forever'
      ? t('coupons.duration.forever')
      : coupon.duration === 'repeating'
        ? t('coupons.duration.months', { count: coupon.durationInMonths ?? 1 })
        : null,
    coupon.minimumLabel ? t('coupons.minimumShort', { amount: coupon.minimumLabel }) : null,
    coupon.description || null,
  ].filter(Boolean).join(' · ')

  const toggle = async (coupon: Coupon) => {
    setBusyId(coupon.id)
    try {
      await updateCoupon(projectId, coupon.id, { ...couponInputOf(coupon), active: !coupon.active }, token)
      showToast(coupon.active ? t('coupons.toast.paused') : t('coupons.toast.resumed'))
      await coupons.refresh()
    } catch (error) {
      showToast(errorMessage(error), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const copy = (value: string, message: string) => {
    void navigator.clipboard?.writeText(value)
    showToast(message)
  }

  const openMenu = (coupon: Coupon, button: HTMLElement) => {
    if (menu?.id === coupon.id) { setMenu(null); return }
    const rect = button.getBoundingClientRect()
    /* Las mismas medidas que el menú de Cobros: ancho fijo del portal y el
       alto de cinco opciones con su separador. */
    const width = 184
    const height = 196
    const up = window.innerHeight - rect.bottom < height + 20 && rect.top > height + 20
    setMenu({
      id: coupon.id,
      top: up ? rect.top - height - 8 : rect.bottom + 8,
      left: Math.max(12, Math.min(window.innerWidth - width - 12, rect.right - width)),
      up,
    })
  }

  const menuCoupon = menu ? rows.find((coupon) => coupon.id === menu.id) : undefined
  const menuLink = menuCoupon && menuCoupon.offerIds.length === 1 && !menuCoupon.appliesToAll
    ? couponShareLink(siteOrigin, offerById.get(menuCoupon.offerIds[0] ?? ''), menuCoupon.code)
    : null

  return (
    <div className="dash-projects-view dash-pages-view dash-coupons-view dash-loaded-content-in">
      <SectionRefresh refreshing={coupons.refreshing} failed={Boolean(coupons.error)} />
      <DashboardPageHeader
        title={t('coupons.title')}
        subtitle={t('coupons.subtitle')}
        actions={(
          <>
            <HeaderRefreshButton refreshing={coupons.refreshing} onRefresh={() => { void coupons.refresh(); loadStatus() }} />
            <button type="button" className="dash-primary-btn" disabled={!connected} onClick={() => setEditing('new')}>
              <IconPlus size={15} /> {t('coupons.new')}
            </button>
          </>
        )}
      />

      <StripeConnectBanner
        hasPaidOffer={offers.data ? hasPaidOffer(offers.data) : null}
        status={status}
        failed={Boolean(statusFailed)}
        detail={statusFailed || undefined}
        canConnect={canConnectStripe}
        onConnect={onOpenStripeSettings}
        onOpenPlans={onOpenPlans}
        onRetry={loadStatus}
      />

      {rows.length > 0 ? (
        <header className="dash-toolbar dash-layered-toolbar dash-coupons-toolbar">
          <div className="dash-toolbar-lead">
            <div className="dash-toolbar-search">
              <span className="dash-toolbar-search-icon" aria-hidden="true"><IconSearch size={16} /></span>
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Escape') setQuery('') }}
              />
            </div>
          </div>
        </header>
      ) : null}

      {coupons.error && coupons.data === undefined ? (
        <div className="dash-inline-msg">
          <span>
            {t('coupons.loadFailed')}
            <code className="dash-failure-detail">{failureDetail(coupons.error)}</code>
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void coupons.refresh() }}>{t('coupons.retry')}</button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="dash-placeholder">
          <IconTicket size={34} aria-hidden="true" />
          <strong>{t('coupons.empty.title')}</strong>
          <span>{t('coupons.empty.body')}</span>
          {connected ? (
            <button type="button" className="dash-primary-btn" onClick={() => setEditing('new')}>
              <IconPlus size={15} /> {t('coupons.new')}
            </button>
          ) : null}
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('coupons.noResults')}</strong>
          <button type="button" className="dash-secondary-btn" onClick={() => setQuery('')}>{t('coupons.clearSearch')}</button>
        </div>
      ) : (
        <div className="dash-cms-table-scroll">
          <div className="dash-data-table dash-coupons-data-table">
            <div className="dash-data-header" role="row">
              <span>{t('coupons.col.code')}</span>
              <span>{t('coupons.col.discount')}</span>
              <span>{t('coupons.col.appliesTo')}</span>
              <span>{t('coupons.col.uses')}</span>
              <span>{t('coupons.col.status')}</span>
              <span>{t('coupons.col.expires')}</span>
              <span className="dash-data-header-actions">{t('coupons.col.actions')}</span>
            </div>
            <div className="dash-project-list">
              {visible.map((coupon) => (
                <article className="dash-project-row dash-data-row dash-loaded-content-in" key={coupon.id} onClick={openOnRowClick(() => setEditing(coupon))}>
                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') setEditing(coupon) }}
                  >
                    <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                      <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
                        <IconTicket size={13} />
                      </span>
                    </span>
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        <strong className="dash-coupon-code">{coupon.code}</strong>
                        {/* Copiar sin abrir el cupón: es lo que se hace con un
                            código —pegarlo en un mensaje—, y la fila entera
                            abre el editor. */}
                        <button
                          type="button"
                          className="dash-coupon-copy-btn"
                          aria-label={t('coupons.copyCodeAria', { code: coupon.code })}
                          data-tooltip={t('coupons.menu.copyCode')}
                          onClick={(event) => { event.stopPropagation(); copy(coupon.code, t('coupons.toast.codeCopied')) }}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <IconCopy size={13} aria-hidden="true" />
                        </button>
                      </span>
                      {detailLabel(coupon) ? <span className="dash-project-row-details">{detailLabel(coupon)}</span> : null}
                    </span>
                  </div>
                  <div className="dash-list-row-meta">
                    <span className="dash-data-cell dash-coupon-discount-cell" data-label={t('coupons.col.discount')}>
                      <strong>{t('coupons.off', { amount: coupon.discountLabel })}</strong>
                    </span>
                    <span className="dash-data-cell dash-coupon-applies-cell" data-label={t('coupons.col.appliesTo')} title={appliesLabel(coupon)}>
                      {appliesLabel(coupon)}
                    </span>
                    <span className="dash-data-cell dash-coupon-uses-cell" data-label={t('coupons.col.uses')} title={t('coupons.usesTitle', { revenue: coupon.revenueLabel, discount: coupon.discountTotalLabel })}>
                      <span className="dash-coupon-uses-compact">{usesCompact(coupon)}</span>
                      <span className="dash-coupon-uses-full">{usesFull(coupon)}</span>
                    </span>
                    <span className="dash-data-cell" data-label={t('coupons.col.status')}>
                      <span className={`dash-offer-status ${STATUS_TONE[coupon.status]}`}>{t(`coupons.status.${coupon.status}`)}</span>
                    </span>
                    <span className="dash-data-cell dash-list-date-cell" data-label={t('coupons.col.expires')}>
                      {coupon.expiresAt ? (
                        <>
                          <IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />
                          {formatDashboardListDate(coupon.expiresAt)}
                        </>
                      ) : <em className="dash-coupon-muted">{t('coupons.noExpiry')}</em>}
                    </span>
                  </div>
                  <div className="dash-project-row-actions">
                    <button
                      type="button"
                      className={`dash-general-toggle${coupon.active ? ' is-on' : ''}`}
                      role="switch"
                      aria-checked={coupon.active}
                      aria-label={t('coupons.toggleAria', { code: coupon.code })}
                      disabled={busyId === coupon.id}
                      onClick={() => { void toggle(coupon) }}
                    >
                      <span />
                    </button>
                    {/* Envuelto igual que en el resto de listas: en el
                        teléfono la fila esconde TODO botón que sea hijo
                        directo de esta esquina —el interruptor incluido, que
                        ahí es de más porque pausar y reanudar ya están en este
                        mismo menú— y sólo deja ver lo que vive dentro de
                        `.dash-project-row-menu-wrap`. Sin el envoltorio el
                        interruptor y los tres puntos son hijos directos por
                        igual, y desaparecían los dos. */}
                    <div className="dash-project-row-menu-wrap">
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('coupons.moreOptions')}
                        data-tooltip={t('coupons.options')}
                        aria-expanded={menu?.id === coupon.id}
                        data-coupon-menu-trigger
                        onClick={(event) => { event.stopPropagation(); openMenu(coupon, event.currentTarget) }}
                      >
                        <IconDots size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {menu && menuCoupon ? createPortal(
        <div ref={menuRef} className={`project-menu dash-project-row-menu dash-coupon-row-menu-portal${menu.up ? ' is-up' : ''}`} role="menu" style={{ top: menu.top, left: menu.left }}>
          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); setEditing(menuCoupon) }}>
            <IconPencil size={14} /> {t('coupons.menu.edit')}
          </button>
          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); copy(menuCoupon.code, t('coupons.toast.codeCopied')) }}>
            <IconCopy size={14} /> {t('coupons.menu.copyCode')}
          </button>
          {menuLink ? (
            <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); copy(menuLink, t('coupons.toast.linkCopied')) }}>
              <IconLink size={14} /> {t('coupons.menu.copyLink')}
            </button>
          ) : null}
          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); void toggle(menuCoupon) }}>
            {menuCoupon.active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
            {menuCoupon.active ? t('coupons.menu.pause') : t('coupons.menu.resume')}
          </button>
          <div className="project-menu-rule" role="separator" />
          <button className="project-menu-item danger" type="button" role="menuitem" onClick={() => { setMenu(null); setConfirmDelete(menuCoupon) }}>
            <IconTrash size={14} /> {t('coupons.menu.delete')}
          </button>
        </div>,
        document.body,
      ) : null}

      {editing ? (
        <CouponDialog
          projectId={projectId}
          token={token}
          coupon={editing === 'new' ? null : editing}
          offers={offerList}
          siteOrigin={siteOrigin}
          onClose={() => setEditing(null)}
          onSaved={(saved, created) => {
            setEditing(null)
            showToast(created ? t('coupons.toast.created', { code: saved.code }) : t('coupons.toast.saved'))
            void coupons.refresh()
          }}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={t('coupons.delete.title', { code: confirmDelete.code })}
          message={t('coupons.delete.body')}
          confirmLabel={t('coupons.delete.confirm')}
          tone="danger"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => {
            try {
              await deleteCoupon(projectId, confirmDelete.id, token)
              showToast(t('coupons.toast.deleted'))
              setConfirmDelete(null)
              await coupons.refresh()
            } catch (error) {
              showToast(errorMessage(error), 'error')
              throw error
            }
          }}
        />
      ) : null}
    </div>
  )
}
